import { useState, useRef, useEffect } from "react";
import { C } from "../constants/colors";
import { css } from "../constants/globalStyles";

const TYPES = ["Open Call", "Exhibition", "Residency", "Grant", "Award"];
const FEES = [
  "No fee. Free to apply and participate.",
  "Entry fee under $50",
  "Entry fee $50 to $250",
  "Entry fee over $250",
];

const PAGE_CSS = `
  html, body {
    margin: 0;
    padding: 0;
    background: #3b2f2f;
    min-height: 100%;
  }
  @keyframes soFadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .so-anim-1 { opacity: 0; animation: soFadeUp 0.5s ease 0.1s forwards; }
  .so-anim-2 { opacity: 0; animation: soFadeUp 0.5s ease 0.2s forwards; }
  .so-anim-3 { opacity: 0; animation: soFadeUp 0.5s ease 0.3s forwards; }
  .so-anim-4 { opacity: 0; animation: soFadeUp 0.5s ease 0.4s forwards; }
  .so-grid-2  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .so-sub-btn { display: block; width: 100%; background: ${C.bark}; color: ${C.parch};
    font-family: 'Fraunces', serif; font-style: italic; font-size: 18px; font-weight: 300;
    border: none; border-bottom: 3px solid ${C.clay}; border-radius: 3px;
    padding: 15px 24px; cursor: pointer; transition: all 0.2s; letter-spacing: 0.02em; }
  .so-sub-btn:hover:not(:disabled) { background: ${C.ink}; transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(30,20,16,0.25); }
  .so-sub-btn:disabled { opacity: 0.7; cursor: not-allowed; }
  @media (max-width: 600px) {
    .so-nav    { padding: 0 24px !important; }
    .so-hero   { padding: 48px 24px !important; }
    .so-form   { padding: 36px 24px 40px !important; }
    .so-footer { padding: 24px 24px !important; }
    .so-grid-2 { grid-template-columns: 1fr; }
    .so-hero h1 { font-size: 38px !important; }
  }
`;

function FadeSection({ children, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ opacity: 0, transform: "translateY(16px)", transition: "opacity 0.5s ease, transform 0.5s ease", ...style }}
    >
      {children}
    </div>
  );
}

export default function SubmitOpportunity() {
  const [type,        setType]        = useState("Open Call");
  const [fee,         setFee]         = useState(FEES[0]);
  const [submitted,   setSubmitted]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [form, setForm] = useState({
    title: "", org: "", location: "", deadline: "",
    mediums: "", eligibility: "", support: "",
    description: "", applyLink: "", contact: "",
  });

  const charCount = form.description.length;
  const setField  = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("https://formspree.io/f/mzdyolwq", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ type, fee, ...form, email: form.contact }),
      });
      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch (err) {
      setSubmitError("Something went wrong. Please try again or email us at hello@opencall.com.au");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setType("Open Call");
    setFee(FEES[0]);
    setForm({ title: "", org: "", location: "", deadline: "", mediums: "", eligibility: "", support: "", description: "", applyLink: "", contact: "" });
    setSubmitError(null);
  };

  // Shared style objects
  const inp = {
    display: "block", width: "100%",
    background: C.cream, border: `1px solid ${C.sand}`,
    borderRadius: 3, padding: "11px 14px",
    fontFamily: "'DM Sans', sans-serif", fontSize: 14,
    color: C.bark, outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  };
  const lbl = {
    display: "block",
    fontFamily: "'DM Mono', monospace", fontSize: 10,
    textTransform: "uppercase", letterSpacing: "0.1em",
    color: C.muted, marginBottom: 6,
  };
  const helper = {
    fontFamily: "'DM Sans', sans-serif", fontSize: 12,
    fontStyle: "italic", color: C.mutedL, marginTop: 4,
  };
  const focusInp = (e) => { e.target.style.borderColor = C.clay; e.target.style.boxShadow = "0 0 0 3px rgba(193,127,74,0.1)"; };
  const blurInp  = (e) => { e.target.style.borderColor = C.sand; e.target.style.boxShadow = "none"; };
  const req = <span style={{ color: C.clay }}>*</span>;

  const SectionHead = ({ num, label }) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, paddingBottom: 14, borderBottom: `1px solid ${C.sand}`, marginBottom: 22 }}>
      <span style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontStyle: "italic", color: C.clay, fontWeight: 300, lineHeight: 1 }}>{num}</span>
      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "2px", color: C.clay }}>{label}</span>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{css}</style>
      <style>{PAGE_CSS}</style>

      {/* Nav */}
      <nav className="so-nav" style={{ background: C.bark, height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 40px", position: "sticky", top: 0, zIndex: 200, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <a href="/" style={{ textDecoration: "none" }}>
          <span style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 400, color: "rgba(245,239,230,0.6)" }}>
            open call<span style={{ color: C.clay }}>.</span>
          </span>
        </a>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "1.5px", color: "rgba(245,239,230,0.3)" }}>
          Submit an opportunity
        </span>
      </nav>

      {/* Hero */}
      <section className="so-hero" style={{ background: C.ink, padding: "64px 40px 56px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <p className="so-anim-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: "2px", color: C.clay, marginBottom: 18 }}>
            For galleries, institutions and organisations
          </p>
          <h1 className="so-anim-2" style={{ fontFamily: "'Fraunces', serif", fontSize: 52, fontWeight: 300, color: "#f5efe6", lineHeight: 1.08, marginBottom: 18 }}>
            Submit your{" "}
            <em style={{ fontStyle: "italic", color: C.clay }}>open call.</em>
          </h1>
          <p className="so-anim-3" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "rgba(245,239,230,0.5)", lineHeight: 1.65, marginBottom: 28 }}>
            We feature Australian opportunities to a growing community of artists. Fill in the details below and we'll post it this week.
          </p>
          <div className="so-anim-4" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TYPES.map(t => (
              <span key={t} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, textTransform: "uppercase", letterSpacing: "1.5px", color: "rgba(245,239,230,0.4)", border: "1px solid rgba(193,127,74,0.3)", borderRadius: 20, padding: "4px 12px" }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Form area */}
      <div className="so-form" style={{ background: C.parch, padding: "48px 40px 40px", flex: 1 }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>

          {submitted ? (
            /* Success state */
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.sageL, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", fontSize: 28, color: C.sageD }}>✓</div>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 36, fontWeight: 300, color: C.bark, lineHeight: 1.15, marginBottom: 16 }}>
                We've got it.{" "}
                <em style={{ fontStyle: "italic", color: C.clay }}>Thank you.</em>
              </h2>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: C.muted, lineHeight: 1.65, maxWidth: 460, margin: "0 auto 32px" }}>
                We review every submission and post approved opportunities to our Instagram. We'll be in touch within 48 hours if we have any questions.
              </p>
              <button onClick={resetForm} style={{ background: "none", border: "none", fontFamily: "'DM Mono', monospace", fontSize: 11, textTransform: "uppercase", letterSpacing: "1.5px", color: C.clay, cursor: "pointer", textDecoration: "underline" }}>
                Submit another opportunity
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>

              {/* Section 01 — About the opportunity */}
              <FadeSection style={{ marginBottom: 40 }}>
                <SectionHead num="01" label="About the opportunity" />

                <div style={{ marginBottom: 22 }}>
                  <label style={lbl}>Type {req}</label>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                    {TYPES.map(t => (
                      <button key={t} type="button" onClick={() => setType(t)} style={{
                        padding: "8px 14px", borderRadius: 3,
                        border: `1px solid ${type === t ? C.clay : C.sand}`,
                        background: type === t ? C.clay : C.cream,
                        color: type === t ? C.cream : C.muted,
                        fontFamily: "'DM Mono', monospace", fontSize: 11,
                        letterSpacing: "1px", textTransform: "uppercase",
                        cursor: "pointer", transition: "all 0.15s",
                      }}>{t}</button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={lbl}>Title {req}</label>
                  <input required style={inp} value={form.title} onChange={setField("title")} placeholder="e.g. Open Exhibition 2025" onFocus={focusInp} onBlur={blurInp} />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={lbl}>Organisation / Gallery {req}</label>
                  <input required style={inp} value={form.org} onChange={setField("org")} placeholder="e.g. The Centre for Contemporary Art" onFocus={focusInp} onBlur={blurInp} />
                </div>

                <div className="so-grid-2" style={{ marginBottom: 18 }}>
                  <div>
                    <label style={lbl}>Location {req}</label>
                    <input required style={inp} value={form.location} onChange={setField("location")} placeholder="e.g. Melbourne, VIC" onFocus={focusInp} onBlur={blurInp} />
                    <p style={helper}>City, state</p>
                  </div>
                  <div>
                    <label style={lbl}>Deadline {req}</label>
                    <input required type="date" style={inp} value={form.deadline} onChange={setField("deadline")} onFocus={focusInp} onBlur={blurInp} />
                    <p style={helper}>Application close date</p>
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={lbl}>Mediums accepted</label>
                  <input style={inp} value={form.mediums} onChange={setField("mediums")} placeholder="e.g. Painting, Photography, Sculpture" onFocus={focusInp} onBlur={blurInp} />
                  <p style={helper}>Leave blank if all mediums welcome</p>
                </div>

                <div>
                  <label style={lbl}>Eligibility</label>
                  <input style={inp} value={form.eligibility} onChange={setField("eligibility")} placeholder="e.g. Open to Australian artists only" onFocus={focusInp} onBlur={blurInp} />
                  <p style={helper}>Who can apply?</p>
                </div>
              </FadeSection>

              {/* Section 02 — Fees and support */}
              <FadeSection style={{ marginBottom: 40 }}>
                <SectionHead num="02" label="Fees and support" />

                <div style={{ marginBottom: 22 }}>
                  <label style={lbl}>Entry fee {req}</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {FEES.map(f => (
                      <button key={f} type="button" onClick={() => setFee(f)} style={{
                        display: "flex", alignItems: "center", gap: 12,
                        width: "100%", padding: "12px 14px",
                        background: fee === f ? "rgba(193,127,74,0.06)" : C.cream,
                        border: `1px solid ${fee === f ? C.clay : C.sand}`,
                        borderRadius: 3, cursor: "pointer", textAlign: "left",
                        transition: "all 0.15s",
                      }}>
                        <span style={{
                          width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                          border: `2px solid ${fee === f ? C.clay : C.mutedL}`,
                          background: fee === f ? C.clay : "transparent",
                          transition: "all 0.15s",
                        }} />
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: fee === f ? C.bark : C.muted }}>
                          {f}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={lbl}>Support / Prize</label>
                  <input style={inp} value={form.support} onChange={setField("support")} placeholder="e.g. $5,000 prize, exhibition space, artist fee" onFocus={focusInp} onBlur={blurInp} />
                  <p style={helper}>What do artists receive? e.g. prize money, exhibition space, stipend</p>
                </div>
              </FadeSection>

              {/* Section 03 — Description and links */}
              <FadeSection style={{ marginBottom: 40 }}>
                <SectionHead num="03" label="Description and links" />

                <div style={{ marginBottom: 18 }}>
                  <label style={lbl}>Description {req}</label>
                  <div style={{ position: "relative" }}>
                    <textarea
                      required
                      maxLength={300}
                      rows={4}
                      style={{ ...inp, resize: "vertical", minHeight: 100, paddingBottom: 28 }}
                      value={form.description}
                      onChange={setField("description")}
                      placeholder="What should artists know about this opportunity?"
                      onFocus={focusInp}
                      onBlur={blurInp}
                    />
                    <span style={{
                      position: "absolute", bottom: 8, right: 10, pointerEvents: "none",
                      fontFamily: "'DM Mono', monospace", fontSize: 10,
                      color: charCount >= 300 ? C.rose : charCount >= 270 ? C.amber : C.mutedL,
                    }}>
                      {charCount} / 300
                    </span>
                  </div>
                  <p style={helper}>Up to 300 characters. What should artists know?</p>
                </div>

                <div>
                  <label style={lbl}>Apply link or email {req}</label>
                  <input required style={inp} value={form.applyLink} onChange={setField("applyLink")} placeholder="https://yourgallery.com/apply or hello@gallery.com" onFocus={focusInp} onBlur={blurInp} />
                  <p style={helper}>Link to application form, website, or submission email</p>
                </div>
              </FadeSection>

              {/* Section 04 — Your contact */}
              <FadeSection style={{ marginBottom: 36 }}>
                <SectionHead num="04" label="Your contact" />
                <div>
                  <label style={lbl}>Email or Instagram {req}</label>
                  <input required style={inp} value={form.contact} onChange={setField("contact")} placeholder="hello@gallery.com or @yourgallery" onFocus={focusInp} onBlur={blurInp} />
                  <p style={helper}>Not published. So we can follow up if we have questions.</p>
                </div>
              </FadeSection>

              {/* Note box */}
              <div style={{ background: C.parchD, borderLeft: `2px solid ${C.sand}`, padding: "14px 16px", marginBottom: 22, borderRadius: "0 3px 3px 0" }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.mutedL, lineHeight: 1.6, margin: 0 }}>
                  We review every submission and post approved opportunities to our Instagram. We'll reach out within 48 hours if we have any questions.
                </p>
              </div>

              {/* Submit button */}
              <button type="submit" disabled={submitting} className="so-sub-btn">
                {submitting ? "Submitting…" : "Submit open call →"}
              </button>

              {submitError && (
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.rose, marginTop: 14, lineHeight: 1.5 }}>
                  {submitError}
                </p>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="so-footer" style={{ background: C.bark, padding: "28px 40px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <span style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 400, color: "rgba(245,239,230,0.6)" }}>
          open call<span style={{ color: C.clay }}>.</span>
        </span>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "1px", color: "rgba(245,239,230,0.4)" }}>opencall.com.au</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "1px", color: "rgba(245,239,230,0.25)", marginTop: 2 }}>Built for Australian artists</div>
        </div>
      </footer>
    </div>
  );
}
